import React, { memo, useEffect, useState } from 'react'
import { CSSTransition } from 'react-transition-group'
import { CheckCircleIcon } from '../../assets/icons/CheckCircleIcon'
import { ExclamationIcon } from '../../assets/icons/ExclamationIcon'
import { TooltipPosition } from '../../libraries/Tooltips'
import { getMergedClassNames, getUniqueKey } from '../../utils/HTMLutils'
import './Etapas.scss'
const rootClassName = 'component-etapas'

interface EtapasProps {
  id?: string
  atual?: number
  setAtual?: (evt?: any) => void
  hideNotActualLabel?: boolean
  etapas?: {
    'tooltip-text'?: string
    'tooltip-position'?: TooltipPosition
    id?: string
    icon?: React.ReactNode
    name?: string
    disabled?: boolean
    error?: boolean
    completed?: boolean
    notification?: boolean
  }[]
  scrollOnChange?: boolean
}

function Etapas(props: EtapasProps) {
  const [id] = useState(props.id || getUniqueKey())
  useEffect(() => {
    const element = document.querySelector(`#${id}`) as HTMLElement
    element.style.cursor =
      element.scrollWidth > element.clientWidth ? 'grab' : 'default'
    let pos = { left: 0, x: 0 }
    let moving = false

    const mouseDownHandler = (evt: any) => {
      if (evt.button === 0 && element.scrollWidth > element.clientWidth) {
        element.style.cursor = 'grabbing'
        moving = true
        pos = {
          left: element.scrollLeft,
          x: evt.clientX
        }
      }
    }
    const mouseOverHandler = () => {
      element.style.cursor =
        element.scrollWidth > element.clientWidth ? 'grab' : 'default'
    }

    const mouseMoveHandler = (evt: any) => {
      const dx = evt.clientX - pos.x

      // Scroll the element
      if (moving) {
        element.scrollLeft = pos.left - dx
      }
    }
    const mouseUpHandler = () => {
      element.style.cursor =
        element.scrollWidth > element.clientWidth ? 'grab' : 'default'
      moving = false
    }
    element.addEventListener('mouseover', mouseOverHandler)
    element.addEventListener('mousedown', mouseDownHandler)
    document.addEventListener('mousemove', mouseMoveHandler)
    document.addEventListener('mouseup', mouseUpHandler)
    return () => {
      element.removeEventListener('mouseover', mouseOverHandler)
      element.removeEventListener('mousedown', mouseDownHandler)
      document.removeEventListener('mousemove', mouseMoveHandler)
      document.removeEventListener('mouseup', mouseUpHandler)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const larguras: number[] = []
    document
      .querySelectorAll(`#${id} .${rootClassName}-etapa`)
      .forEach((etapa) => {
        larguras.push(parseInt(window.getComputedStyle(etapa).width))
      })
    const maior = Math.max(...larguras)
    document
      .querySelectorAll(`#${id} .${rootClassName}-etapa`)
      .forEach((etapa: HTMLElement) => {
        etapa.style.width = `${maior}px`
      })
  })

  useEffect(() => {
    const outerComponent = document.querySelector(`#${id}`)
    const atual = document.querySelector(`#${id} .${rootClassName}-etapa.atual`)
    if (props.scrollOnChange && outerComponent && atual) {
      atual.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'center'
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.atual])

  return (
    <div className={rootClassName} id={id}>
      <div className={`${rootClassName}-etapas`}>
        {props.etapas && props.etapas.length
          ? props.etapas.map((etapa, index) => {
              const p = {
                ...etapa,
                className: getMergedClassNames([
                  `${rootClassName}-etapa`,
                  props.atual === index ? 'atual' : '',
                  etapa.error ? 'error' : '',
                  etapa.completed ? 'completed' : '',
                  etapa.disabled ? 'disabled' : ''
                ])
              }
              delete p.disabled
              delete p.icon
              delete p.error
              delete p.completed
              delete p.name
              return (
                <button
                  {...p}
                  key={index}
                  onClick={() => {
                    if (
                      !etapa.disabled &&
                      typeof props.setAtual === 'function' &&
                      props.atual !== index
                    ) {
                      props.setAtual(index)
                    }
                  }}
                >
                  <div
                    id={etapa.id || undefined}
                    className={getMergedClassNames([
                      `${rootClassName}-icon`,
                      etapa.completed ? 'completed' : '',
                      etapa.error ? 'error' : '',
                      etapa.notification ? 'notification' : ''
                    ])}
                  >
                    {etapa.icon}
                  </div>
                  <div
                    className={getMergedClassNames([
                      `${rootClassName}-label`,
                      'nunito',
                      etapa.error ? 'error' : '',
                      etapa.completed ? 'completed' : '',
                      props.hideNotActualLabel ? 'hide' : ''
                    ])}
                  >
                    <CSSTransition
                      in={etapa.error}
                      timeout={300}
                      classNames={`${rootClassName}-appear`}
                      unmountOnExit
                    >
                      <ExclamationIcon />
                    </CSSTransition>
                    <CSSTransition
                      in={etapa.completed}
                      timeout={300}
                      classNames={`${rootClassName}-appear`}
                      unmountOnExit
                    >
                      <CheckCircleIcon />
                    </CSSTransition>
                    {etapa.name}
                  </div>
                </button>
              )
            })
          : undefined}
      </div>
    </div>
  )
}

Etapas.defaultProps = {
  etapas: [],
  atual: 0,
  scrollOnChange: true
}

export default memo(Etapas)
